// src/ConsumerApp/Screens/Account/components/CancelOrderModal.js

import React, { useState, useEffect } from 'react';
import X from '../../../../assets/icons/X.svg';

export default function CancelOrderModal({
  isOpen,
  onClose,
  onConfirm,
  isPending = false,
  orderNumber = '',
}) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      setReason('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isReasonValid = reason.trim().length > 0;

  const handleSubmit = () => {
    if (!isReasonValid || isPending) return;
    onConfirm(reason.trim());
  };

  return (
    <>
      <style>{`
        .bb-cancel-modal-overlay {
          position: fixed;
          inset: 0;
          background-color: rgba(15, 23, 42, 0.6);
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
          backdrop-filter: blur(3px);
          padding: 16px;
          box-sizing: border-box;
          animation: bbFadeIn 0.15s ease-out;
        }

        @keyframes bbFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .bb-cancel-sheet-card {
          background-color: #FFFFFF;
          border-radius: 16px;
          padding: 24px;
          width: 100%;
          max-width: 480px;
          box-shadow: 0 16px 40px rgba(15, 23, 42, 0.2);
          display: flex;
          flex-direction: column;
          gap: 16px;
          box-sizing: border-box;
          animation: bbScaleUp 0.18s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes bbScaleUp {
          from { transform: scale(0.96); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        .bb-cancel-textarea {
          width: 100%;
          height: 105px;
          border-radius: 10px;
          border: 1.5px solid #CBD5E1;
          padding: 12px 14px;
          font-size: 13.5px;
          color: #0F172A;
          outline: none;
          resize: none;
          font-family: inherit;
          box-sizing: border-box;
          background-color: #F8FAFC;
          transition: border-color 0.15s ease, background-color 0.15s ease;
        }

        .bb-cancel-textarea:focus {
          border-color: #E9001E;
          background-color: #FFFFFF;
        }

        .bb-cancel-actions-row {
          display: flex;
          align-items: center;
          gap: 12px;
          justify-content: flex-end;
          width: 100%;
        }

        @media (max-width: 640px) {
          .bb-cancel-modal-overlay {
            align-items: flex-end !important;
            padding: 0 !important;
          }

          .bb-cancel-sheet-card {
            max-width: 100% !important;
            border-bottom-left-radius: 0 !important;
            border-bottom-right-radius: 0 !important;
            border-top-left-radius: 20px !important;
            border-top-right-radius: 20px !important;
            padding: 20px 18px !important;
            padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px)) !important;
            animation: bbSlideUp 0.22s cubic-bezier(0.16, 1, 0.3, 1) !important;
          }

          @keyframes bbSlideUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }

          .bb-cancel-actions-row {
            flex-direction: column-reverse !important;
            gap: 10px !important;
          }

          .bb-cancel-actions-row button {
            width: 100% !important;
            height: 44px !important;
            font-size: 14px !important;
          }
        }
      `}</style>

      <div
        className="bb-cancel-modal-overlay"
        onClick={() => {
          if (!isPending) onClose();
        }}
      >
        <div className="bb-cancel-sheet-card" onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h4 style={{ margin: 0, fontSize: 18, fontWeight: '800', color: '#0F172A' }}>
                Cancel Order {orderNumber ? `#${orderNumber}` : ''}
              </h4>
              <span style={{ fontSize: 13, color: '#64748B', display: 'block', marginTop: 4 }}>
                Please let us know why you are cancelling this order.
              </span>
            </div>
            <button
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
              onClick={() => {
                if (!isPending) onClose();
              }}
              disabled={isPending}
              type="button"
              aria-label="Close modal"
            >
              <img src={X} alt="close" style={{ width: 14, height: 14, opacity: 0.6 }} />
            </button>
          </div>

          <div style={{ position: 'relative' }}>
            <textarea
              className="bb-cancel-textarea"
              placeholder="e.g., Ordered by mistake, found a better price, delivery timeline too long..."
              maxLength={300}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isPending}
            />
            <span
              style={{
                position: 'absolute',
                bottom: 8,
                right: 12,
                fontSize: 11,
                color: '#94A3B8',
                fontWeight: '600',
                pointerEvents: 'none',
              }}
            >
              {reason.length}/300
            </span>
          </div>

          <div className="bb-cancel-actions-row">
            <button
              type="button"
              style={styles.cancelBtn}
              onClick={onClose}
              disabled={isPending}
            >
              Keep Order
            </button>
            <button
              type="button"
              style={{
                ...styles.confirmBtn,
                opacity: !isReasonValid || isPending ? 0.6 : 1,
                cursor: !isReasonValid || isPending ? 'not-allowed' : 'pointer',
              }}
              onClick={handleSubmit}
              disabled={!isReasonValid || isPending}
            >
              {isPending ? 'Cancelling...' : 'Confirm Cancellation'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

const styles = {
  cancelBtn: {
    border: '1px solid #CBD5E1',
    backgroundColor: '#FFFFFF',
    color: '#334155',
    padding: '9px 18px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: '700',
    cursor: 'pointer',
  },
  confirmBtn: {
    border: 'none',
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    padding: '9px 18px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: '800',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(233, 0, 30, 0.25)',
  },
};